import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowRight,
  Cat,
  Filter,
  PawPrint,
  RotateCcw,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

export default function ChatsAdoptionPage() {
  const cardsRef = useRef([]);
  const [cats, setCats] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    sex: "Tous",
    age: "Tous",
    sterilized: "Tous",
    urgentOnly: false,
  });

  useEffect(() => {
    fetch("/data/adoptable-cats.json")
      .then((res) => res.json())
      .then(setCats)
      .catch(() => setCats([]));
  }, []);

  const ageOptions = useMemo(() => {
    return ["Tous", "Moins de 2 ans", "2 à 8 ans", "9 ans et +"];
  }, []);

  const getAgeCategory = (cat) => {
    const label = cat.ageLabel || "";
    const age = parseInt(label, 10);

    if (Number.isNaN(age)) return "Tous";
    if (age < 2) return "Moins de 2 ans";
    if (age <= 8) return "2 à 8 ans";
    return "9 ans et +";
  };

  const filteredCats = useMemo(() => {
    return cats.filter((cat) => {
      const search = filters.search.trim().toLowerCase();

      const searchOk =
        !search ||
        cat.name?.toLowerCase().includes(search) ||
        cat.race?.toLowerCase().includes(search) ||
        cat.shortDescription?.toLowerCase().includes(search) ||
        cat.personality?.some((item) => item.toLowerCase().includes(search));

      const sexOk = filters.sex === "Tous" || cat.sex === filters.sex;
      const ageOk = filters.age === "Tous" || getAgeCategory(cat) === filters.age;

      const sterilizedOk =
        filters.sterilized === "Tous" ||
        (filters.sterilized === "Oui" && cat.sterilized === true) ||
        (filters.sterilized === "Non" && cat.sterilized === false);

      const urgentOk = !filters.urgentOnly || cat.urgent;

      return searchOk && sexOk && ageOk && sterilizedOk && urgentOk;
    });
  }, [cats, filters]);

  useEffect(() => {
    if (!filteredCats.length) return;

    cardsRef.current.forEach((card) => {
      if (!card) return;

      gsap.fromTo(
        card,
        { opacity: 0, y: 45, filter: "blur(8px)" },
        {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 88%",
          },
        }
      );
    });

    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, [filteredCats]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      sex: "Tous",
      age: "Tous",
      sterilized: "Tous",
      urgentOnly: false,
    });
  };

  const getSterilizedLabel = (value) => {
    if (value === null || value === undefined) return "À confirmer";
    return value ? "Stérilisé(e)" : "Non stérilisé(e)";
  };

  const FilterPanel = () => (
    <div className="rounded-[2rem] border border-white/70 bg-white/80 p-5 shadow-[0_24px_80px_rgba(0,0,0,0.05)] backdrop-blur-xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-black/35">
            Recherche avancée
          </p>
          <h2 className="mt-2 text-2xl font-semibold text-[#111111]">
            Filtres
          </h2>
        </div>

        <button
          type="button"
          onClick={resetFilters}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-black/55 transition hover:bg-red-50 hover:text-red-600"
          aria-label="Réinitialiser les filtres"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-6 space-y-6">
        <div>
          <label className="mb-2 block text-xs uppercase tracking-[0.22em] text-black/40">
            Rechercher
          </label>

          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-black/35" />
            <input
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
              placeholder="Nom, race, caractère..."
              className="w-full rounded-2xl border border-black/10 bg-[#faf7f3] py-4 pl-11 pr-4 text-sm outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-50"
            />
          </div>
        </div>

        <FilterGroup
          label="Sexe"
          value={filters.sex}
          options={["Tous", "Femelle", "Mâle"]}
          onChange={(value) => updateFilter("sex", value)}
        />

        <FilterGroup
          label="Âge"
          value={filters.age}
          options={ageOptions}
          onChange={(value) => updateFilter("age", value)}
        />

        <FilterGroup
          label="Stérilisation"
          value={filters.sterilized}
          options={["Tous", "Oui", "Non"]}
          onChange={(value) => updateFilter("sterilized", value)}
        />

        <button
          type="button"
          onClick={() => updateFilter("urgentOnly", !filters.urgentOnly)}
          className={`flex w-full items-center justify-between rounded-2xl border px-4 py-4 text-sm font-semibold transition ${
            filters.urgentOnly
              ? "border-red-500 bg-red-600 text-white shadow-[0_14px_40px_rgba(220,38,38,0.2)]"
              : "border-black/10 bg-[#faf7f3] text-black/60 hover:bg-red-50 hover:text-red-600"
          }`}
        >
          Profils urgents uniquement

          <span
            className={`h-5 w-9 rounded-full p-1 transition ${
              filters.urgentOnly ? "bg-white/25" : "bg-black/10"
            }`}
          >
            <span
              className={`block h-3 w-3 rounded-full transition ${
                filters.urgentOnly
                  ? "translate-x-4 bg-white"
                  : "translate-x-0 bg-black/35"
              }`}
            />
          </span>
        </button>
      </div>
    </div>
  );

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f8f5f0] text-[#111111]">
      <Helmet>
        <title>Chats à adopter | Savoinimaux</title>
        <meta
          name="description"
          content="Découvrez les chats actuellement proposés à l’adoption par Savoinimaux en Savoie et Haute-Savoie."
        />
        <link rel="canonical" href="https://savoinimaux.com/adoption" />
      </Helmet>

      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(180deg,#fcfaf7_0%,#f8f4ee_45%,#f1ebe3_100%)]" />
        <div className="absolute -left-48 top-0 h-[520px] w-[520px] rounded-full bg-red-100/70 blur-3xl" />
        <div className="absolute right-[-180px] top-40 h-[600px] w-[600px] rounded-full bg-[#e8d5c8]/70 blur-3xl" />
      </div>

      <section className="relative mx-auto max-w-7xl px-6 pb-14 pt-20 lg:px-10">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-white/80 px-4 py-2 text-[11px] uppercase tracking-[0.32em] text-red-600 shadow-sm backdrop-blur-xl">
            <Cat className="h-4 w-4" />
            Adoption
          </div>

          <h1 className="mt-6 text-5xl font-semibold leading-[0.95] tracking-[-0.04em] md:text-7xl">
            Nos chats
            <span className="block bg-gradient-to-r from-red-600 to-[#ff6b57] bg-clip-text text-transparent">
              à adopter.
            </span>
          </h1>

          <p className="mt-6 max-w-3xl text-base leading-8 text-black/62 md:text-lg">
            Une sélection de chats à l’adoption est présentée directement sur le site.
            <br className="hidden md:block" />
            <span className="font-medium text-[#111111]">
              Les chatons, les urgences et les nouvelles arrivées sont publiés uniquement sur Facebook et Instagram
            </span>{" "}
            afin de partager les informations plus rapidement et toucher un maximum de personnes.
          </p>
        </div>

        <div className="mt-10 flex items-center justify-between rounded-[1.6rem] border border-white/70 bg-white/75 p-4 shadow-[0_18px_60px_rgba(0,0,0,0.04)] backdrop-blur-xl lg:hidden">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-black/35">
              Résultats
            </p>
            <p className="mt-1 text-sm font-semibold text-black/70">
              {filteredCats.length} chat{filteredCats.length > 1 ? "s" : ""} affiché
              {filteredCats.length > 1 ? "s" : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_40px_rgba(220,38,38,0.22)]"
          >
            <Filter className="h-4 w-4" />
            Filtrer
          </button>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <FilterPanel />

              <div className="mt-5 rounded-[1.5rem] border border-white/70 bg-white/65 p-5 text-sm leading-7 text-black/55 shadow-sm backdrop-blur-xl">
                <p className="font-semibold text-black/75">
                  {filteredCats.length} chat{filteredCats.length > 1 ? "s" : ""} trouvé
                  {filteredCats.length > 1 ? "s" : ""}
                </p>
                <p className="mt-1">
                  Ajustez les critères pour trouver un compagnon adapté à votre foyer.
                </p>
              </div>
            </div>
          </aside>

          <div>
            <div className="grid gap-8 xl:grid-cols-2">
              {filteredCats.map((cat, index) => (
                <Link
                  key={cat.slug}
                  to={`/adoption/${cat.slug}`}
                  ref={(el) => (cardsRef.current[index] = el)}
                  className="group relative overflow-hidden rounded-[2.2rem] border border-white/70 bg-white/80 p-4 shadow-[0_24px_80px_rgba(0,0,0,0.05)] backdrop-blur-xl transition duration-500 hover:-translate-y-2 hover:shadow-[0_36px_100px_rgba(220,38,38,0.13)]"
                >
                  <div className="relative h-[340px] overflow-hidden rounded-[1.7rem]">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.045]"
                      loading="lazy"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />

                    {cat.urgent && (
                      <span className="absolute left-4 top-4 rounded-full bg-red-600 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-lg">
                        Urgent
                      </span>
                    )}

                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-xs uppercase tracking-[0.28em] text-white/72">
                        {cat.race}
                      </p>
                      <h2 className="mt-2 text-3xl font-semibold text-white">
                        {cat.name}
                      </h2>
                    </div>
                  </div>

                  <div className="px-2 py-5">
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-600">
                        {cat.sex}
                      </span>

                      <span className="rounded-full bg-black/[0.04] px-3 py-1 text-xs font-semibold text-black/55">
                        {cat.ageLabel || "Âge à confirmer"}
                      </span>

                      <span className="rounded-full bg-black/[0.04] px-3 py-1 text-xs font-semibold text-black/55">
                        {getSterilizedLabel(cat.sterilized)}
                      </span>

                      {cat.fiv && (
                        <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                          FIV+
                        </span>
                      )}
                    </div>

                    <p className="mt-4 min-h-[64px] text-sm leading-7 text-black/58">
                      {cat.shortDescription}
                    </p>

                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-red-600">
                      Voir sa fiche
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {!filteredCats.length && (
              <div className="rounded-[2rem] border border-white/70 bg-white/80 p-10 text-center shadow-sm">
                <PawPrint className="mx-auto h-10 w-10 text-red-300" />
                <p className="mt-4 font-semibold">
                  Aucun chat ne correspond aux filtres.
                </p>
                <button
                  onClick={resetFilters}
                  className="mt-5 rounded-full bg-red-600 px-5 py-3 text-sm font-semibold text-white"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {filtersOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          <button
            type="button"
            aria-label="Fermer les filtres"
            onClick={() => setFiltersOpen(false)}
            className="absolute inset-0 bg-black/45 backdrop-blur-sm"
          />

          <div className="absolute bottom-0 left-0 right-0 max-h-[88vh] overflow-y-auto rounded-t-[2rem] bg-[#f8f5f0] p-5 shadow-[0_-20px_80px_rgba(0,0,0,0.22)]">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-black/70">
                <SlidersHorizontal className="h-4 w-4 text-red-600" />
                Filtres d’adoption
              </div>

              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-black/70"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <FilterPanel />

            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="mt-5 w-full rounded-full bg-red-600 px-6 py-4 text-sm font-semibold text-white shadow-[0_14px_40px_rgba(220,38,38,0.22)]"
            >
              Voir les {filteredCats.length} résultat
              {filteredCats.length > 1 ? "s" : ""}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

function FilterGroup({ label, value, options, onChange }) {
  return (
    <div>
      <p className="mb-3 text-xs uppercase tracking-[0.22em] text-black/40">
        {label}
      </p>

      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`rounded-2xl px-4 py-3 text-sm font-semibold transition ${
              value === option
                ? "bg-red-600 text-white shadow-[0_12px_30px_rgba(220,38,38,0.2)]"
                : "border border-black/10 bg-[#faf7f3] text-black/58 hover:bg-red-50 hover:text-red-600"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}